import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Field } from '../../components/ui/Field'
import { FormCard } from '../../components/ui/FormCard'
import { PageTitle } from '../../components/ui/PageTitle'
import { settingsSchema } from '../../schemas/adminSchemas'
import { api } from '../../lib/api'

type Settings = z.infer<typeof settingsSchema>

export function SettingsPage() {
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  const form = useForm<Settings>({
    resolver: zodResolver(settingsSchema),
    mode: 'onBlur',
    defaultValues: {
      storeName: '',
      deliveryCharge: 40,
      freeDeliveryThreshold: 999,
      deliveryPromise: '24 hours',
      enquiryPhone: '',
      supportPhone: '',
      supportEmail: 'support@seasonly.in',
    },
  })

  useEffect(() => {
    api.get('/admin/settings').then(({ data }) => {
      const s = data.settings
      form.reset({
        storeName: s.storeName,
        deliveryCharge: s.deliveryCharge,
        freeDeliveryThreshold: s.freeDeliveryThreshold,
        deliveryPromise: s.deliveryPromise,
        enquiryPhone: s.enquiryPhone,
        supportPhone: s.supportPhone,
        supportEmail: s.supportEmail,
      })
      setAdminName(s.admin?.name ?? '')
      setAdminEmail(s.admin?.email ?? '')
    })
  }, [form])

  const onSubmit = async (values: Settings) => {
    setSaving(true)
    setMessage('')
    try {
      await api.put('/admin/settings', { ...values, currentPassword: currentPassword || undefined, newPassword: newPassword || undefined })
      setMessage('Settings saved successfully')
      setCurrentPassword('')
      setNewPassword('')
    } catch (err: any) {
      setMessage(err?.response?.data?.message || 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageTitle title="Profile Settings" subtitle="Edit the admin profile and the essential shop settings." />
      <FormCard title="Edit Profile" submitLabel={saving ? 'Saving…' : 'Save Settings'} onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field className="bg-stone-50 text-stone-500" disabled label="Admin Name" value={adminName} onChange={() => {}} />
          <Field className="bg-stone-50 text-stone-500" disabled label="Admin Email" value={adminEmail} onChange={() => {}} />
          <Field label="Store Name" error={form.formState.errors.storeName?.message} {...form.register('storeName')} />
          <Field label="Delivery Charge" type="number" error={form.formState.errors.deliveryCharge?.message} {...form.register('deliveryCharge', { valueAsNumber: true })} />
          <Field label="Free Delivery Threshold" type="number" error={form.formState.errors.freeDeliveryThreshold?.message} {...form.register('freeDeliveryThreshold', { valueAsNumber: true })} />
          <Field as="select" label="Delivery Promise" error={form.formState.errors.deliveryPromise?.message} {...form.register('deliveryPromise')}>
            <option>12 hours</option><option>24 hours</option><option>48 hours</option>
          </Field>
          <Field label="Enquiry Phone" error={form.formState.errors.enquiryPhone?.message} {...form.register('enquiryPhone')} />
          <Field label="Support Phone" error={form.formState.errors.supportPhone?.message} {...form.register('supportPhone')} />
          <Field label="Support Email" error={form.formState.errors.supportEmail?.message} {...form.register('supportEmail')} />
          <label className="block text-sm font-semibold text-leaf-dark">
            Current Password
            <input className="mt-2 w-full rounded-lg border border-stone-200 bg-white px-3 py-2.5 text-sm text-stone-900 outline-none transition focus:border-mango-orange focus:ring-4 focus:ring-mango-yellow/25" type="password" autoComplete="new-password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
          </label>
          <PasswordField label="New Password" value={newPassword} onChange={setNewPassword} show={showNewPassword} toggle={() => setShowNewPassword(v => !v)} />
        </div>
        {message && <p className={`mt-2 text-sm ${message.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{message}</p>}
      </FormCard>
    </div>
  )
}

function PasswordField({ label, value, onChange, show, toggle }: { label: string; value: string; onChange: (v: string) => void; show: boolean; toggle: () => void }) {
  return (
    <label className="block text-sm font-semibold text-leaf-dark">
      {label}
      <div className="mt-2 flex rounded-lg border border-stone-200 bg-white focus-within:border-mango-orange focus-within:ring-4 focus-within:ring-mango-yellow/25">
        <input className="min-w-0 flex-1 rounded-l-lg px-3 py-2.5 text-sm text-stone-900 outline-none" type={show ? 'text' : 'password'} value={value} onChange={e => onChange(e.target.value)} />
        <button className="grid w-11 place-items-center rounded-r-lg text-stone-500 hover:bg-stone-50 hover:text-leaf-dark" onClick={toggle} type="button">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </label>
  )
}
